import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FilePdf, MusicNote, Image as ImageIcon, VideoCamera, X } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const FILE_ICONS = { pdf: FilePdf, audio: MusicNote, image: ImageIcon, video: VideoCamera };
const FILE_LABELS = { pdf: 'PDF', audio: 'Áudio', image: 'Imagem', video: 'Vídeo' };

// P1 — Conteúdo de hoje (cliente): lista os ficheiros disponíveis para o dia actual do desafio.
// PDFs abrem em nova tab; áudio/vídeo tocam inline; imagens abrem em lightbox.
export default function ChallengeContentOfDay({ challengeId, dayNum }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {url, title}

  const fetchFiles = useCallback(async () => {
    if (!challengeId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/challenges/${challengeId}/files`);
      setFiles(Array.isArray(data) ? data : []);
    } catch {
      setFiles([]);
    } finally { setLoading(false); }
  }, [challengeId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  if (!challengeId) return null;

  const filesByDay = files.reduce((acc, f) => {
    const d = Number(f.day) || 1;
    if (!acc[d]) acc[d] = [];
    acc[d].push(f);
    return acc;
  }, {});
  const sortedDays = Object.keys(filesByDay).map(Number).sort((a, b) => b - a);

  const fileUrl = (id) => `${API}/api/challenge-files/${id}/download`;

  const handleOpen = async (file) => {
    if (file.file_type === 'image') {
      try {
        const resp = await axios.get(fileUrl(file.id), { responseType: 'blob' });
        const blob = URL.createObjectURL(resp.data);
        setLightbox({ url: blob, title: file.title });
      } catch { toast.error('Erro a abrir imagem'); }
      return;
    }
    if (file.file_type === 'pdf') {
      // PDFs abrem em nova tab via blob (preserva cookie de auth)
      try {
        const resp = await axios.get(fileUrl(file.id), { responseType: 'blob' });
        const blob = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
        window.open(blob, '_blank');
      } catch { toast.error('Erro a abrir PDF'); }
      return;
    }
    // audio/video — handled inline by <audio>/<video> elements below
  };

  if (loading) return null;
  if (files.length === 0) return null;

  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4 mb-4" data-testid="challenge-content-of-day">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-medium text-sm text-brand-text">Conteúdo do desafio</h3>
        <span className="text-[10px] text-brand-muted">Dia {dayNum}</span>
      </div>
      <div className="space-y-3">
        {sortedDays.map(d => (
          <div key={d}>
            <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-1.5">{d === dayNum ? 'Hoje' : `Dia ${d}`}</p>
            <div className="space-y-2">
              {filesByDay[d].map(f => {
                const Icon = FILE_ICONS[f.file_type] || FilePdf;
                const inlineMedia = f.file_type === 'audio' || f.file_type === 'video';
                return (
                  <div key={f.id} className="border border-brand-border rounded-xl p-3 bg-brand-bg/30" data-testid={`challenge-file-${f.id}`}>
                    <div className="flex items-start gap-2.5">
                      <Icon size={20} className="text-brand-green flex-shrink-0 mt-0.5" weight="duotone" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-brand-text truncate">{f.title}</p>
                        <p className="text-[10px] text-brand-muted mt-0.5">{FILE_LABELS[f.file_type] || f.file_type}</p>
                      </div>
                      {!inlineMedia && (
                        <button onClick={() => handleOpen(f)} className="text-[10px] bg-brand-green text-white rounded-full px-3 py-1 flex-shrink-0" data-testid={`open-file-${f.id}`}>
                          {f.file_type === 'pdf' ? 'Abrir' : 'Ver'}
                        </button>
                      )}
                    </div>
                    {f.file_type === 'audio' && (
                      <audio controls preload="metadata" className="w-full mt-2 rounded">
                        <source src={fileUrl(f.id)} type={f.content_type || 'audio/mpeg'} />
                      </audio>
                    )}
                    {f.file_type === 'video' && (
                      <video controls preload="metadata" className="w-full mt-2 rounded-xl max-h-72 bg-black">
                        <source src={fileUrl(f.id)} type={f.content_type || 'video/mp4'} />
                      </video>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {lightbox && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4" onClick={() => { URL.revokeObjectURL(lightbox.url); setLightbox(null); }} data-testid="lightbox">
          <button className="absolute top-4 right-4 text-white" onClick={(e) => { e.stopPropagation(); URL.revokeObjectURL(lightbox.url); setLightbox(null); }} data-testid="lightbox-close"><X size={28} /></button>
          <img src={lightbox.url} alt={lightbox.title} className="max-h-full max-w-full object-contain rounded-2xl" />
        </div>
      )}
    </div>
  );
}
